// frontend/src/services/upsDriverService.js
// Responsibility: Talk to the backend and return driver-shaped objects. Centralize all UPS API calls + normalization.
// Refactored Phase 8.7B: "Reads" for Active/Reserved now belong to assignmentService. 
// This service now handles: 
// 1. Fetching Banned drivers (global list).
// 2. All "Write" actions (Reserve, Activate, Ban, Unban).
import axios from 'axios';

/* ---------- helpers ---------- */
const pick = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
  return undefined;
};
const toNum = v => {
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

/** Ensure Active/Reserved rows carry a `status` field and expected props */
// Kept because actions like 'activateDriver' still return a single driver row that needs this normalization.
const tagStatus = (rows, status) =>
  (Array.isArray(rows) ? rows : (rows?.rows || [])).map(d => {
    const id        = toNum(pick(d.id, d.driver_id, d.driverId, d.driver?.id));
    const agency_id = toNum(pick(d.agency_id, d.agencyId, d.agency?.id));

    return {
      ...d,
      id,
      driver_id: id,
      status,
      name:  pick(d.name, d.driver_name, d.driver?.name, ''),
      email: pick(d.email, d.driver?.email, ''),
      phone: pick(d.phone, d.driver?.phone, ''),
      vehicle_registration_number: pick(
        d.vehicle_registration_number,
        d.vehicleRegistrationNumber,
        d.vrn,
        d.vehicle_reg,
        d.driver?.vehicle_registration_number,
        ''
      ),
      agency_id,
      agency_name: pick(d.agency_name, d.agencyName, d.agency?.name, ''),
      submission_date: pick(d.submission_date, d.created_at, d.submitted_at, d.driver?.submission_date, null),
    };
  });

/**
 * Convert ban records → driver-shaped entities
 */
const normalizeBanned = (rows) =>
  (Array.isArray(rows) ? rows : (rows?.rows || [])).map(r => {
    const driverIdFromDto = (r && 'ban_id' in r && !('driver_id' in r)) ? r.id : undefined;

    const driver_id = toNum(pick(
      r.driver_id,
      r.driverId,
      r.driver?.id,
      driverIdFromDto
    ));
    const agency_id = toNum(pick(r.agency_id, r.agencyId, r.agency?.id));
    const ban_id    = toNum(pick(r.ban_id, r.id));

    return {
      ...r, 
      id: driver_id,
      driver_id,
      status: 'Banned',
      name:  pick(r.name, r.driver_name, r.driver?.name, ''),
      email: pick(r.email, r.driver?.email, ''),
      phone: pick(r.phone, r.driver?.phone, ''),
      vehicle_registration_number: pick(
        r.vehicle_registration_number,
        r.vehicleRegistrationNumber,
        r.vrn,
        r.vehicle_reg,
        r.driver?.vehicle_registration_number,
        ''
      ),
      agency_id,
      agency_name: pick(r.agency_name, r.agencyName, r.agency?.name, ''),
      submission_date: pick(r.submission_date, r.driver?.submission_date, null),
      ban_id,
      ban_reason: pick(r.ban_reason, r.reason, ''),
      banned_at: pick(r.created_at, r.banned_at, null),
    };
  });

/* ---------- FETCHING (READS) ---------- */

/**
 * Fetch UPS drivers by status. 
 * ✅ CHANGED Phase 8.7B: Throws error for Active/Reserved/Confirmed.
 * Only handles 'Banned' reading now.
 */
export async function fetchDriversByStatus(status, { agencyId, search } = {}) {
  // Guardrail: Force devs to use assignmentService for site lists
  if (['active', 'reserved', 'confirmed', 'Active', 'Reserved'].includes(status)) {
    throw new Error(
       `[upsDriverService] fetchDriversByStatus is deprecated for '${status}'. Use fetchSiteAssignments(siteId, ...) instead.`
    );
  }

  const params = {};
  if (agencyId) params.agency_id = agencyId;
  if (search)   params.search    = search;

  // We only expect 'Banned' (or 'banned') to pass through here
  // Adjust endpoint path if your backend expects lowercase 'banned'
  const safeStatus = status.toLowerCase() === 'banned' ? 'banned' : status;

  const { data } = await axios.get(`/ups/drivers/${safeStatus}`, { params });

  if (safeStatus === 'banned') {
    return normalizeBanned(data);
  }
  return tagStatus(data, status);
}

/**
 * Fetch the UPS audit list of banned drivers.
 */
export async function fetchBannedDrivers({ agencyId, search } = {}) {
  // Reuse the logic above
  return fetchDriversByStatus('banned', { agencyId, search });
}

// REMOVED: fetchAllStatuses (Legacy Dashboard Read)

/* ---------- ACTIONS (WRITES) ---------- */

/** Reserve an active driver → returns a normalized driver ready for upsert */
export async function reserveDriver(id, siteKey = 'MAIN') {
  const { data } = await axios.put(`/ups/sites/${siteKey}/drivers/${id}/reserved`);
  const [normalized] = tagStatus([data], 'Reserved');
  return normalized ?? { ...data, status: 'Reserved', id: data.id ?? data.driver_id ?? id };
}

/** Activate a reserved driver → normalized */
export async function activateDriver(id, siteKey = 'MAIN') {
  const { data } = await axios.put(`/ups/sites/${siteKey}/drivers/${id}/active`);
  const [normalized] = tagStatus([data], 'Active');
  return normalized ?? { ...data, status: 'Active', id: data.id ?? data.driver_id ?? id };
}

/**
 * Ban a driver → server returns a full driver DTO + ban_id.
 */
export async function banDriver(driverId, banReason) {
  const payload = { driver_id: driverId, ban_reason: banReason };
  const { data } = await axios.post('/ups/ban-driver', payload);

  if (data && data.id && !('driver_id' in data)) {
    const [normalized] = tagStatus([data], 'Banned');
    return normalized
      ? {
          ...normalized,
          ban_id: data.ban_id ?? normalized.ban_id,
          ban_reason: data.ban_reason ?? normalized.ban_reason,
          banned_at: data.banned_at ?? normalized.banned_at,
        }
      : { ...data, id: data.id, status: 'Banned', banned_at: data.banned_at, ban_id: data.ban_id };
  }

  const [normalized] = normalizeBanned([data]);
  return normalized ?? { id: driverId, status: 'Banned' };
}

/** Update an existing ban record's reason */
export async function updateBan(banId, banReason) {
  const { data } = await axios.put(`/ups/ban-driver/${banId}`, { ban_reason: banReason });

  if (data && data.id && !('driver_id' in data)) {
    const [normalized] = tagStatus([data], 'Banned');
    return normalized ? { ...normalized, ban_id: data.ban_id ?? normalized.ban_id } : null;
  }

  const [normalized] = normalizeBanned([data]);
  return normalized ?? null;
}

/** Unban a driver */
export async function unbanDriver(banIdOrDriverId) {
  const { data } = await axios.put(`/ups/drivers/unban/${banIdOrDriverId}`);
  const status = data?.status ?? 'Reserved';
  const maybeDriver = tagStatus([data], status)[0];
  return maybeDriver ?? {
    ...(data && typeof data === 'object' ? data : {}),
    id: data?.driver_id ?? data?.id ?? toNum(banIdOrDriverId),
    status,
  };
}